import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import api from '@/api'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'Landing',
      component: () => import('@/views/Landing.vue'),
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/Login.vue'),
    },
    {
      path: '/register',
      name: 'Register',
      component: () => import('@/views/Register.vue'),
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/Dashboard.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/messages',
      name: 'Messages',
      component: () => import('@/components/learning/TutorMessaging.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/courses',
      name: 'CourseList',
      component: () => import('@/views/CourseList.vue'),
    },
    {
      path: '/courses/:id',
      name: 'CourseDetail',
      component: () => import('@/views/CourseDetail.vue'),
    },
    {
      path: '/courses/:id/learn',
      name: 'CourseLearn',
      component: () => import('@/views/CourseLearn.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/my-courses',
      name: 'MyCourses',
      component: () => import('@/views/MyCourses.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/instructor',
      name: 'InstructorDashboard',
      component: () => import('@/views/InstructorDashboard.vue'),
      meta: { requiresAuth: true, requiresInstructor: true },
    },
    // Events & Blog
    {
      path: '/events',
      name: 'Events',
      component: () => import('@/views/Events.vue'),
    },
    {
      path: '/events/:id',
      name: 'EventDetail',
      component: () => import('@/views/EventDetail.vue'),
    },
    {
      path: '/my-tickets',
      name: 'MyTickets',
      component: () => import('@/views/MyTickets.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/my-credits',
      name: 'MyCredits',
      component: () => import('@/views/student/CreditApplication.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/my-assignments',
      name: 'MyAssignments',
      component: () => import('@/views/student/StudentAssignments.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/my-program/:program_id',
      name: 'MyProgramDashboard',
      component: () => import('@/views/student/StudentProgramDashboard.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/mentor/dashboard',
      name: 'MentorDashboard',
      component: () => import('@/views/mentor/MentorDashboard.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/blog',
      name: 'Blog',
      component: () => import('@/views/Blog.vue'),
    },
    {
      path: '/blog/:slug',
      name: 'BlogPost',
      component: () => import('@/views/BlogPost.vue'),
    },
    {
      path: '/pricing',
      name: 'Pricing',
      component: () => import('@/views/Pricing.vue'),
    },
    {
      path: '/billing',
      name: 'Billing',
      component: () => import('@/views/Billing.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/community',
      name: 'Community',
      component: () => import('@/views/Community.vue'),
      meta: { requiresAuth: true },
    },
    // Static Pages
    {
      path: '/about',
      name: 'About',
      component: () => import('@/views/About.vue'),
    },
    {
      path: '/contact',
      name: 'Contact',
      component: () => import('@/views/Contact.vue'),
    },
    {
      path: '/credits/pricing',
      name: 'AICreditsInfo',
      component: () => import('@/views/AICreditsInfo.vue'),
    },
    {
      path: '/features',
      name: 'Features',
      component: () => import('@/views/Features.vue'),
    },
    {
      path: '/ai-study',
      name: 'AIStudy',
      component: () => import('@/views/AIStudy.vue'),
    },
    {
      path: '/ai-tutor',
      name: 'AITutorIntro',
      component: () => import('@/views/ai/AITutorIntro.vue'),
    },
    {
      path: '/ai-flashcards',
      name: 'AIFlashcardsIntro',
      component: () => import('@/views/ai/AIFlashcardsIntro.vue'),
    },
    {
      path: '/ai-study-planner',
      name: 'AIStudyPlannerIntro',
      component: () => import('@/views/ai/AIStudyPlannerIntro.vue'),
    },
    {
      path: '/ai-gamification',
      name: 'AIGamificationIntro',
      component: () => import('@/views/ai/AIGamificationIntro.vue'),
    },
    {
      path: '/search',
      name: 'GlobalSearch',
      component: () => import('@/views/GlobalSearch.vue'),
    },
    {
      path: '/weekly-report',
      name: 'WeeklyReport',
      component: () => import('@/views/WeeklyReport.vue'),
    },
    // Admission page temporarily disabled
    // {
    //   path: '/admission',
    //   name: 'Admission',
    //   component: () => import('@/views/Admission.vue'),
    // },
    // Program Pages (paid students only)
    {
      path: '/programs',
      name: 'Programs',
      component: () => import('@/views/Programs.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/programs/acupuncture-shen-disorders',
      name: 'AcupunctureShenDisorders',
      component: () => import('@/views/programs/AcupunctureShenDisorders.vue'),
      meta: { requiresAuth: true },
    },
    // Admin Management (single-tenant, no /organization prefix)
    {
      path: '/admin',
      name: 'AdminDashboard',
      component: () => import('@/views/organization/Dashboard.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/analytics',
      name: 'Analytics',
      component: () => import('@/views/organization/Analytics.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/system',
      name: 'SystemMonitor',
      component: () => import('@/views/organization/SystemMonitor.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/security',
      name: 'SecurityMonitor',
      component: () => import('@/views/organization/SecurityMonitor.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/courses',
      name: 'CourseManager',
      component: () => import('@/views/organization/CourseManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/events',
      name: 'EventManager',
      component: () => import('@/views/organization/EventManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/finance',
      name: 'Finance',
      component: () => import('@/views/organization/Finance.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/forms',
      name: 'FormBuilder',
      component: () => import('@/views/organization/FormBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/forms/editor',
      name: 'FormEditor',
      component: () => import('@/components/admin/FormEditor.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/forms/editor/:id',
      name: 'FormEditorEdit',
      component: () => import('@/components/admin/FormEditor.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/apps',
      name: 'AdminApps',
      component: () => import('@/views/organization/Apps.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/content',
      name: 'AdminContent',
      component: () => import('@/views/organization/Content.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/certificates',
      name: 'CertificateManager',
      component: () => import('@/views/organization/CertificateManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/categories',
      name: 'CategoryManager',
      component: () => import('@/components/admin/CategoryManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/tags',
      name: 'TagManager',
      component: () => import('@/components/admin/TagManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/site',
      name: 'AdminSite',
      component: () => import('@/views/organization/SiteBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/speakers',
      name: 'SpeakerManager',
      component: () => import('@/views/organization/SpeakerManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/organizations',
      name: 'OrgManager',
      component: () => import('@/views/organization/OrgManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/students',
      name: 'Students',
      component: () => import('@/views/organization/Students.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/participants',
      name: 'Participants',
      component: () => import('@/views/organization/Participants.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/access',
      name: 'AccessControl',
      component: () => import('@/views/organization/Members.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/marketing',
      name: 'Marketing',
      component: () => import('@/views/organization/Marketing.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/seo',
      name: 'SEOSettings',
      component: () => import('@/views/organization/Settings.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/settings',
      name: 'AdminSettings',
      component: () => import('@/views/organization/Settings.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/settings/email',
      name: 'EmailSettings',
      component: () => import('@/views/organization/EmailSettings.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/buddy-config',
      name: 'BuddyConfig',
      component: () => import('@/views/organization/BuddyConfig.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/chat-qa',
      name: 'ChatQAManager',
      component: () => import('@/views/organization/ChatQAManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/library',
      name: 'MediaLibraryAdmin',
      component: () => import('@/views/organization/Library.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Communication & Study Programs
    {
      path: '/admin/communication',
      name: 'Communication',
      component: () => import('@/views/organization/Communication.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/programs',
      name: 'StudyPrograms',
      component: () => import('@/views/organization/StudyPrograms.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/programs/:id',
      name: 'StudyProgramDetail',
      component: () => import('@/views/organization/StudyPrograms.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/assignments',
      name: 'AssignmentManager',
      component: () => import('@/views/organization/AssignmentManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Payment Settings
    {
      path: '/admin/settings/payments',
      name: 'PaymentSettings',
      component: () => import('@/views/organization/PaymentSettings.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Tax Settings
    {
      path: '/admin/settings/tax',
      name: 'TaxSettings',
      component: () => import('@/views/organization/TaxSettings.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/agents',
      name: 'AgentsManager',
      component: () => import('@/views/admin/AgentsManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/settings/ai-hub',
      name: 'AIHub',
      component: () => import('@/views/admin/AIHub.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Redirect old scattered AI settings to the unified hub
    { path: '/admin/settings/ai-models', redirect: '/admin/settings/ai-hub' },
    { path: '/admin/settings/ai-model-bank', redirect: '/admin/settings/ai-hub' },
    { path: '/admin/settings/ai-embedding', redirect: '/admin/settings/ai-hub' },
    { path: '/admin/settings/ai-tts', redirect: '/admin/settings/ai-hub' },
    { path: '/admin/settings/ai-stt', redirect: '/admin/settings/ai-hub' },
    { path: '/admin/settings/ai-buddy-config', redirect: '/admin/settings/ai-hub' },
    { path: '/admin/settings/ai-credits', redirect: '/admin/settings/ai-hub' },
    {
      path: '/admin/settings/subscription-plans',
      name: 'SubscriptionPlansAdmin',
      component: () => import('@/views/admin/SubscriptionPlans.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/tcm-ingestion',
      name: 'TCMMasterIngestion',
      component: () => import('@/views/admin/TCMMasterIngestion.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/blog/subscribers',
      name: 'BlogSubscribersAdmin',
      component: () => import('@/views/admin/BlogSubscribers.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/credits',
      name: 'AICreditPlans',
      component: () => import('@/views/AICreditPlans.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/community',
      name: 'CommunityManager',
      component: () => import('@/views/organization/CommunityManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Page Manager
    {
      path: '/admin/pages/:type',
      name: 'PageManager',
      component: () => import('@/views/organization/PageManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Page Builder
    {
      path: '/admin/page-builder',
      name: 'PageBuilder',
      component: () => import('@/components/admin/PageBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Redirects Manager
    {
      path: '/admin/redirects',
      name: 'RedirectsManager',
      component: () => import('@/components/admin/RedirectsManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // Game Manager
    {
      path: '/admin/games',
      name: 'GameManager',
      component: () => import('@/components/admin/GameManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/games/editor/:id',
      name: 'GameEditor',
      component: () => import('@/components/admin/GameEditor.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // AI Buddy System Routes
    {
      path: '/buddies/marketplace',
      name: 'BuddyMarketplace',
      component: () => import('@/views/buddies/BuddyMarketplace.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/buddies/builder',
      name: 'BuddyBuilder',
      component: () => import('@/views/buddies/BuddyBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/buddies/builder/:id',
      name: 'BuddyBuilderEdit',
      component: () => import('@/views/buddies/BuddyBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/buddies/chat/:id',
      name: 'BuddyChat',
      component: () => import('@/views/buddies/BuddyChat.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/buddies/profile/:id',
      name: 'BuddyProfile',
      component: () => import('@/views/buddies/BuddyProfile.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/buddies/guide',
      name: 'BuddyGuide',
      component: () => import('@/views/buddies/BuddyGuide.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/buddies',
      name: 'BuddyLanding',
      component: () => import('@/views/buddies/BuddyLanding.vue'),
    },
    {
      path: '/settings',
      name: 'UserSettings',
      component: () => import('@/views/Settings.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/profile',
      name: 'StudentProfile',
      component: () => import('@/views/StudentProfile.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/profile/edit',
      name: 'MyProfile',
      component: () => import('@/views/MyProfile.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/certificates',
      name: 'Certificates',
      component: () => import('@/views/Certificates.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/progress',
      name: 'StudentProgress',
      component: () => import('@/views/learning/StudentProgressDashboard.vue'),
      meta: { requiresAuth: true },
    },

    // Enhanced Learning Features
    {
      path: '/notebook',
      name: 'StudentNotebook',
      component: () => import('@/views/learning/StudentNotebook.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/flashcards',
      name: 'Flashcards',
      component: () => import('@/views/learning/FlashcardsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/gamification',
      name: 'GamificationProfile',
      component: () => import('@/views/learning/GamificationProfile.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/quiz/:id',
      name: 'QuizTaker',
      component: () => import('@/views/learning/QuizTakerView.vue'),
      meta: { requiresAuth: true },
    },
    // Module Management
    {
      path: '/admin/modules',
      name: 'ModuleManager',
      component: () => import('@/views/organization/ModuleManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // NEW LMS Components - Content Pools
    {
      path: '/admin/lesson-pool',
      name: 'LessonPool',
      component: () => import('@/components/admin/LessonPoolManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/question-bank',
      name: 'QuestionBank',
      component: () => import('@/components/admin/QuestionBankManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/quiz-builder',
      name: 'QuizBuilder',
      component: () => import('@/components/admin/QuizBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/course-builder',
      name: 'CourseBuilder',
      component: () => import('@/components/admin/CourseBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/courses/:id/builder',
      name: 'CourseBuilderAdmin',
      component: () => import('@/views/admin/CourseBuilderAdmin.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/events/:id/edit',
      name: 'EventBuilderAdmin',
      component: () => import('@/views/admin/EventBuilderAdmin.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/blog',
      name: 'BlogPagesManager',
      component: () => import('@/components/admin/BlogPagesManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/menus',
      name: 'MenuManager',
      component: () => import('@/components/admin/MenuManager.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    {
      path: '/admin/homepage',
      name: 'HomepageBuilder',
      component: () => import('@/components/admin/HomepageBuilder.vue'),
      meta: { requiresAuth: true, requiresOwner: true },
    },
    // NEW LMS Components - Learning Experience
    {
      path: '/course/:id/learn',
      name: 'CourseLearning',
      component: () => import('@/views/learning/CourseLearningView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/my-flashcards',
      name: 'MyFlashcardDecks',
      component: () => import('@/components/learning/FlashcardDeckManager.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tutor-messages',
      name: 'TutorMessaging',
      component: () => import('@/components/learning/TutorMessaging.vue'),
      meta: { requiresAuth: true },
    },
    // Gamification & Games
    {
      path: '/game/:id',
      name: 'GamePlayer',
      component: () => import('@/components/games/GamePlayer.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/leaderboard',
      name: 'Leaderboard',
      component: () => import('@/views/LeaderboardView.vue'),
      meta: { requiresAuth: true },
    },
    // Dynamic CMS Pages (must be before catch-all)
    {
      path: '/:slug',
      name: 'DynamicPage',
      component: () => import('@/views/DynamicPage.vue'),
    },
    // Redirect old /organization/* URLs to /admin/* for backward compatibility
    {
      path: '/organization/:pathMatch(.*)*',
      redirect: to => {
        return { path: '/admin/' + to.params.pathMatch }
      }
    },
  ],
})

// Check LMS local auth session
async function checkLMSAuth(authStore: ReturnType<typeof useAuthStore>): Promise<boolean> {
  try {
    // First check local token
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')
    
    if (token && userStr) {
      const user = JSON.parse(userStr)
      // Update auth store with local user data
      authStore.user = user
      authStore.token = token
      return true
    }
    
    // If no local token, try to get session from LMS backend
    const response = await fetch('/api/auth/get-session', {
      method: 'GET',
      headers: { 
        'Accept': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.user) {
        authStore.user = data.user
        return true
      }
    }
  } catch (error) {
    console.warn('[LMS] Auth check failed:', error)
  }
  return false
}

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const hostname = window.location.hostname
  
  // Special handling for buddy.kuos.ai domain
  if (hostname === 'buddy.kuos.ai') {
    // Redirect root to /buddies for buddy subdomain
    if (to.path === '/') {
      next({ name: 'BuddyLanding' })
      return
    }
  }
  
  // Only check authentication for protected routes
  if (to.meta.requiresAuth) {
    let isAuthenticated = authStore.isAuthenticated
    
    // Check LMS local auth session only for protected routes
    if (!isAuthenticated) {
      isAuthenticated = await checkLMSAuth(authStore)
    }
    
    if (!isAuthenticated) {
      // Not authenticated - redirect to local login with return URL
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }
    
    // Check if user is owner and redirect away from student pages
    const userEmail = authStore.user?.email
    const isOwner = userEmail === 'eucm@kuos.ai'
    
    // Redirect owner from student dashboard to admin panel
    if (isOwner && to.path === '/dashboard') {
      next({ path: '/admin/courses' })
      return
    }
    
    // Check if route requires owner access
    if (to.meta.requiresOwner) {
      if (!isOwner) {
        // Redirect to regular dashboard if not owner
        next({ name: 'Dashboard' })
        return
      }
      
      // Redirect owner from /admin to /admin/courses as default
      if (to.path === '/admin') {
        next({ path: '/admin/courses' })
        return
      }
    }
    
    next()
    return
  }
  
  // For public routes: Don't auto-check auth, don't auto-login
  // If user is already logged in (from previous session), they stay logged in
  // But we don't check/create new sessions automatically
  
  next()
})

// ── Dynamic SEO Meta Tags ──
const defaultTitle = 'EUCM - European University of Chinese Medicine'
const defaultDescription = 'Study Traditional Chinese Medicine with expert instructors. Join accredited TCM courses, workshops, and study tours at EUCM.'

const routeMeta: Record<string, { title: string; description: string }> = {
  'Landing': { title: defaultTitle, description: defaultDescription },
  'CourseList': { title: `Courses | ${defaultTitle}`, description: 'Browse our comprehensive TCM courses in acupuncture, herbal medicine, Qigong, and more.' },
  'CourseDetail': { title: `Course | ${defaultTitle}`, description: 'Explore course details, lessons, and enroll today.' },
  'Events': { title: `Events | ${defaultTitle}`, description: 'Join upcoming TCM workshops, congresses, and study tours.' },
  'EventDetail': { title: `Event | ${defaultTitle}`, description: 'Event details and registration.' },
  'Programs': { title: `Programs | ${defaultTitle}`, description: 'Master-level TCM programs with ECTS credits.' },
  'Blog': { title: `Blog | ${defaultTitle}`, description: 'Latest news and articles from EUCM.' },
  'About': { title: `About | ${defaultTitle}`, description: 'Learn about EUCM and our mission to bring TCM to the world.' },
  'Contact': { title: `Contact | ${defaultTitle}`, description: 'Get in touch with EUCM.' },
  'Features': { title: `Features | ${defaultTitle}`, description: 'Discover everything EUCM offers for TCM education.' },
  'AICreditsInfo': { title: `AI Credits & Pricing | ${defaultTitle}`, description: 'Learn how AI credits work and choose a plan that fits your study style.' },
  'Login': { title: `Log In | ${defaultTitle}`, description: 'Sign in to continue your TCM learning journey.' },
  'Register': { title: `Sign Up | ${defaultTitle}`, description: 'Create your free EUCM account and start learning TCM today.' },
  'AIStudy': { title: `AI Study Assistant | ${defaultTitle}`, description: 'Discover AI-powered learning tools for TCM — adaptive paths, AI tutor, flashcards, and more.' },
  'AITutorIntro': { title: `AI Study Tutor | ${defaultTitle}`, description: 'Your personal TCM tutor that knows every lesson, answers questions, and guides you through complex concepts.' },
  'AIFlashcardsIntro': { title: `AI Flashcards | ${defaultTitle}`, description: 'Auto-generated flashcards from your course material. Master TCM with spaced repetition.' },
  'AIStudyPlannerIntro': { title: `AI Study Planner | ${defaultTitle}`, description: 'AI builds your personalized daily study schedule. Balances courses and prioritizes weak topics.' },
  'AIGamificationIntro': { title: `AI Gamification | ${defaultTitle}`, description: 'Turn TCM studies into a game. Earn achievements, maintain streaks, and climb the leaderboard.' },
}

function updateMeta(name: string) {
  const meta = routeMeta[name]
  if (!meta) return
  document.title = meta.title
  let descTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
  if (!descTag) {
    descTag = document.createElement('meta')
    descTag.name = 'description'
    document.head.appendChild(descTag)
  }
  descTag.content = meta.description
}

let sessionId = localStorage.getItem('analytics_session_id')
if (!sessionId) {
  try {
    sessionId = crypto.randomUUID()
  } catch {
    sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
  }
  localStorage.setItem('analytics_session_id', sessionId)
}

import { hasAnalyticsConsent } from '@/utils/cookieConsent'

router.afterEach((to) => {
  const name = to.name as string
  updateMeta(name)

  // Track page view only with consent (fire and forget)
  if (hasAnalyticsConsent() && !to.path.startsWith('/admin') && !to.path.startsWith('/login')) {
    api.post('/analytics/track', {
      page_path: to.path,
      page_title: typeof to.name === 'string' ? to.name : to.path,
      session_id: sessionId,
      referrer: document.referrer
    }).catch(() => {})
  }
})

export default router
